"use client";

import type { WhiteboardSessionBoard } from "@/hooks/useWhiteboard";
import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import styles from "./LessonBoardDock.module.css";
import { WhiteboardCard } from "./WhiteboardCard";
import { statusMeta } from "./WhiteboardRecapGallery";

function ThreadItem({ board }: { board: WhiteboardSessionBoard }) {
  const [open, setOpen] = useState(false);
  const reviewId = useId();
  const meta = statusMeta(board);
  return (
    <li className={styles.threadItem}>
      <button
        type="button"
        className={styles.threadToggle}
        aria-expanded={open}
        aria-controls={open ? reviewId : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={styles.threadLabel}>{board.result.label}</span>
        <span className={meta.className}>{meta.label}</span>
        <ChevronDown
          size={16}
          aria-hidden="true"
          data-open={open || undefined}
          className={styles.chevron}
        />
      </button>
      {open ? (
        // Earlier boards are for review: the server no longer accepts their answers.
        <div id={reviewId} className={styles.threadReview} inert>
          <WhiteboardCard spec={board.spec} embedded />
        </div>
      ) : null}
    </li>
  );
}

/** Earlier boards from this lesson, newest first, folded to one line each. */
export function LessonBoardThread({
  boards,
  currentId,
}: {
  boards: WhiteboardSessionBoard[];
  currentId?: string | null;
}) {
  const headingId = useId();
  const earlier = boards
    .filter((board) => board.spec.id !== currentId && board.result.label)
    .reverse();
  if (!earlier.length) return null;
  return (
    <section aria-labelledby={headingId} className={styles.thread} data-lesson-thread>
      <h2 id={headingId} className={styles.sectionTitle}>
        Earlier in this lesson
      </h2>
      <ul className={styles.threadList}>
        {earlier.map((board) => (
          <ThreadItem key={board.spec.id} board={board} />
        ))}
      </ul>
    </section>
  );
}
